// features/workstations/dashboard-sections/morning-routine.js
// ═══════════════════════════════════════════════════════════
// MORNING ROUTINE — pre-market discipline widget
// ═══════════════════════════════════════════════════════════
// Tier B rituals. Pairs with Process Checklist (which fires
// per-trade during session). This one fires ONCE per morning
// and enforces the prep sequence.
//
// Default items (hardcoded here; editable in a future pass):
//   - Mental state check-in
//   - News scan
//   - Levels reviewed
//   - Setups identified
//   - Thesis stated
//   - Risk budget checked
//   - \"Ready\" tag
//
// Ready button enables only when all required items are ticked.
// Once marked ready, the card collapses to a \"Ready · HH:MM\"
// confirmation so you can tell at a glance whether you skipped
// the routine.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PractitionerLens,
  MetaField,
} from '../../../components/widgets/primitives';

// Default checklist items. Future: editable in config.
// `required: true` gates the Ready button.
const DEFAULT_ITEMS = [
  { id: 'mental_state',  label: 'Mental state check-in',      hint: 'Honest read on focus, mood, sleep', required: true },
  { id: 'news_scan',     label: 'News scan',                   hint: 'Overnight + pre-market catalysts',  required: true },
  { id: 'levels',        label: 'Key levels reviewed',         hint: 'HOD / LOD / pre-market range / prior close', required: true },
  { id: 'setups',        label: 'Valid setups identified',     hint: 'Which of your setups are live today', required: true },
  { id: 'thesis',        label: 'Today’s thesis stated',   hint: 'One-liner: what you think the session does', required: true },
  { id: 'risk_budget',   label: 'Risk budget checked',         hint: 'Where you stand on the week + month', required: false },
  { id: 'distractions',  label: 'Distractions cleared',        hint: 'Phone, slack, news feed — off for the window', required: false },
];

const MENTAL_STATE_LABELS = {
  1: 'Off — step away',
  2: 'Low — cautious day',
  3: 'Balanced',
  4: 'Sharp',
  5: 'Peak',
};

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function MorningRoutineSection({ workstation, userId }) {
  const [log, setLog] = useState(null);
  const [history, setHistory] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mentalStateEditing, setMentalStateEditing] = useState(false);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
      });
      const res = await fetch(`/api/workstations/morning-routine?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setLog(payload?.log || null);
      setHistory(Array.isArray(payload?.history) ? payload.history : []);
      setToday(payload?.today || null);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  const patch = useCallback(async (body) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/workstations/morning-routine', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstation.id,
          user_id: userId,
          ...body,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return false;
      }
      setLog(data.log || null);
      return true;
    } catch (e) {
      setError(e?.message || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [workstation?.id, userId]);

  return (
    <section className="ws-routine">
      {loading && <div className="ws-routine-loading">Loading…</div>}

      {!loading && (
        <RoutineBody
          log={log}
          history={history}
          today={today}
          saving={saving}
          error={error}
          mentalStateEditing={mentalStateEditing}
          setMentalStateEditing={setMentalStateEditing}
          onToggleItem={(id) => patch({ toggle_item: id })}
          onMentalState={(score, note) => {
            patch({ mental_state_score: score, mental_state_note: note });
          }}
          onNotes={(text) => patch({ notes: text })}
          onMarkReady={() => patch({ mark_ready: true })}
          onUnmarkReady={() => patch({ mark_ready: false })}
        />
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function RoutineBody({
  log, history, today, saving, error,
  mentalStateEditing, setMentalStateEditing,
  onToggleItem, onMentalState, onNotes,
  onMarkReady, onUnmarkReady,
}) {
  const completed = new Set(log?.completed_items || []);
  const requiredDone = DEFAULT_ITEMS.filter(i => i.required).every(i => completed.has(i.id));
  const totalDone = DEFAULT_ITEMS.filter(i => completed.has(i.id)).length;
  const totalCount = DEFAULT_ITEMS.length;
  const isReady = !!log?.ready_tagged_at;

  const progressPct = Math.round((totalDone / totalCount) * 100);

  const headline = isReady
    ? `Ready · ${fmtTime(log.ready_tagged_at)}`
    : requiredDone
      ? 'Ready to tag.'
      : 'Prep in progress.';
  const headlineTone = isReady ? 'sage' : requiredDone ? 'gold' : 'steel';

  const summary = isReady
    ? 'Morning routine tagged. The session can start clean.'
    : `${totalDone} of ${totalCount} checked · ${progressPct}% complete. Mark the required items to enable the Ready tag.`;

  return (
    <>
      <Eyebrow parts={[
        'morning routine',
        today,
        isReady ? 'READY' : requiredDone ? 'ready to tag' : `${progressPct}%`,
      ]} />

      <SerifHeadline>
        <span className={`ws-routine-tone-${headlineTone}`}>{headline}</span>
      </SerifHeadline>
      <SerifSummary>{summary}</SerifSummary>

      {error && <div className="ws-routine-error">{error}</div>}

      {/* Mental state check-in row */}
      <SectionStrip label="mental state">
        <MentalStateRow
          score={log?.mental_state_score}
          note={log?.mental_state_note}
          editing={mentalStateEditing}
          setEditing={setMentalStateEditing}
          saving={saving}
          onSave={onMentalState}
        />
      </SectionStrip>

      {/* Checklist */}
      <SectionStrip label="checklist" count={`${totalDone}/${totalCount}`}>
        <div className="ws-routine-list">
          {DEFAULT_ITEMS.map((item) => (
            <ChecklistItem
              key={item.id}
              item={item}
              checked={completed.has(item.id)}
              saving={saving}
              onToggle={() => onToggleItem(item.id)}
            />
          ))}
        </div>
      </SectionStrip>

      {/* Ready button + history strip */}
      <div className="ws-routine-ready-row">
        {!isReady ? (
          <button
            type="button"
            className="ws-routine-btn ws-routine-btn-sage"
            onClick={onMarkReady}
            disabled={!requiredDone || saving}
            title={requiredDone ? 'Tag the routine complete' : 'Check required items first'}
          >
            {saving ? '…' : '✓ mark ready'}
          </button>
        ) : (
          <button
            type="button"
            className="ws-routine-btn ws-routine-btn-steel"
            onClick={onUnmarkReady}
            disabled={saving}
            title="Undo ready tag"
          >
            undo ready
          </button>
        )}

        <HistoryStrip history={history} today={today} />
      </div>

      {/* Notes (optional) */}
      <NotesField
        value={log?.notes || ''}
        saving={saving}
        onSave={onNotes}
      />

      <PractitionerLens label="why this widget exists">
        Pre-market drift is invisible until a red session traces back to a
        skipped step. The routine tag creates a checkable record that
        future-you can look back on — “every red day this month,
        I hadn’t marked ready by 9:30.” That’s edge data.
      </PractitionerLens>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// MENTAL STATE ROW
// ═══════════════════════════════════════════════════════════
function MentalStateRow({ score, note, editing, setEditing, saving, onSave }) {
  const [localScore, setLocalScore] = useState(score || 3);
  const [localNote, setLocalNote] = useState(note || '');

  useEffect(() => {
    setLocalScore(score || 3);
    setLocalNote(note || '');
  }, [score, note]);

  if (!editing) {
    if (score == null) {
      return (
        <button
          type="button"
          className="ws-routine-mental-btn ws-routine-mental-empty"
          onClick={() => setEditing(true)}
          disabled={saving}
        >
          + log mental state
        </button>
      );
    }
    const label = MENTAL_STATE_LABELS[score] || '—';
    return (
      <button
        type="button"
        className="ws-routine-mental-btn"
        onClick={() => setEditing(true)}
        disabled={saving}
      >
        <div className="ws-routine-mental-score-row">
          <MentalStateDots score={score} />
          <span className="ws-routine-mental-label">{label}</span>
        </div>
        {note && <div className="ws-routine-mental-note">{note}</div>}
      </button>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const ok = await onSave(localScore, localNote || null);
    if (ok) setEditing(false);
  };

  return (
    <form className="ws-routine-mental-form" onSubmit={submit}>
      <div className="ws-routine-mental-picker">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={`ws-routine-mental-chip ${n === localScore ? 'is-active' : ''}`}
            onClick={() => setLocalScore(n)}
          >
            {n} · {MENTAL_STATE_LABELS[n].split(' — ')[0]}
          </button>
        ))}
      </div>
      <textarea
        className="ws-routine-textarea"
        rows={2}
        value={localNote}
        onChange={(e) => setLocalNote(e.target.value)}
        placeholder="Optional note — slept 5hrs, kid woke up early, headache, etc."
      />
      <div className="ws-routine-form-actions">
        <button type="submit" disabled={saving} className="ws-routine-btn ws-routine-btn-sage">
          {saving ? 'saving…' : 'save'}
        </button>
        <button type="button" onClick={() => setEditing(false)} disabled={saving} className="ws-routine-btn ws-routine-btn-steel">
          cancel
        </button>
      </div>
    </form>
  );
}

function MentalStateDots({ score }) {
  return (
    <span className="ws-routine-mental-dots">
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`ws-routine-mental-dot ${n <= (score || 0) ? 'is-filled' : ''}`}
          aria-hidden
        />
      ))}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════
// CHECKLIST ITEM
// ═══════════════════════════════════════════════════════════
function ChecklistItem({ item, checked, saving, onToggle }) {
  return (
    <button
      type="button"
      className={`ws-routine-item ${checked ? 'is-checked' : ''}`}
      onClick={onToggle}
      disabled={saving}
    >
      <span className={`ws-routine-item-check ${checked ? 'is-checked' : ''}`} aria-hidden>
        {checked ? '✓' : ''}
      </span>
      <span className="ws-routine-item-body">
        <span className="ws-routine-item-label">
          {item.label}
          {item.required && <span className="ws-routine-item-required" aria-hidden>*</span>}
        </span>
        {item.hint && <span className="ws-routine-item-hint">{item.hint}</span>}
      </span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════
// HISTORY STRIP
// ═══════════════════════════════════════════════════════════
function HistoryStrip({ history, today }) {
  if (!history || history.length === 0) return null;
  // Render in chronological order (oldest → newest), up to 10
  const items = [...history].reverse().slice(-10);
  return (
    <div className="ws-routine-history">
      <span className="ws-routine-history-label">last 10</span>
      <div className="ws-routine-history-dots">
        {items.map((h) => {
          const isToday = h.log_date === today;
          const ready = !!h.ready_tagged_at;
          const done = (h.completed_items || []).length;
          const tone = ready ? 'sage' : done > 0 ? 'amber' : 'dim';
          return (
            <span
              key={h.log_date}
              className={`ws-routine-history-dot ws-routine-history-dot-${tone} ${isToday ? 'is-today' : ''}`}
              title={`${h.log_date} · ${ready ? 'ready' : `${done} items`}${h.mental_state_score ? ` · state ${h.mental_state_score}/5` : ''}`}
            />
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// NOTES FIELD
// ═══════════════════════════════════════════════════════════
function NotesField({ value, saving, onSave }) {
  const [local, setLocal] = useState(value);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setLocal(value); setDirty(false); }, [value]);

  const submit = async () => {
    await onSave(local);
    setDirty(false);
  };

  return (
    <MetaField label="notes">
      <textarea
        className="ws-routine-textarea ws-routine-textarea-notes"
        rows={2}
        value={local}
        onChange={(e) => { setLocal(e.target.value); setDirty(true); }}
        onBlur={() => { if (dirty) submit(); }}
        placeholder="Anything the checklist missed — session plan, specific watch list, whatever."
      />
    </MetaField>
  );
}

// ─── Helpers ───
function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
